import api from '@common/api';
import logging from '@common/logging';
import session from '@common/session';
import { AddBankAccountViaPlaidMutationVariables, BankDetail, InvestorAccount } from '@services/appsync/types';
import { getCurrentUser } from 'aws-amplify/auth';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import create from 'zustand';
import { BankAccountsModel } from '../../../../common/models/BankAccountsModel';
import { InvestorAccountModel } from '../../../../common/models/InvestorAccountModel';
import { StepInfo, SurveyBannerState } from '../types';

const BLOCKED_BANK_ACCOUNT_MESSAGE = 'This bank or financial institution is blocked';

interface SurveyBannerModelProps {
  currentStep: number;
  id?: string;
  steps: SurveyBannerState[];
  isAuthenticated: boolean;
  loading: boolean;
}
interface SurveyBannerModelActions {
  addBankAccount: (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => Promise<{ success: boolean; error?: string }>;

  determineAccountSetupState: () => void;
  determineSurveyBannerState(
    bankAccounts: BankDetail[],
    investorAccount: InvestorAccount | null,
    viewed?: SurveyBannerState[],
  ): {
    current: 'ADD_BANK_ACCOUNT' | 'CONFIGURE_INVESTOR' | 'STARTING_YOUR_PROFILE' | 'VERIFY_IDENTITY' | null;
    steps: SurveyBannerState[];
  };
  getStepInfo(step: SurveyBannerState | null, existingUser?: boolean): StepInfo | null;
  nextStep: () => void;
  prevStep: () => void;
  setSteps: (steps: SurveyBannerState[]) => void;
  setLoading: (loading: boolean) => void;
}

export function determineSurveyBannerState(
  bankAccounts: BankDetail[],
  investorAccount: InvestorAccount | null,
  viewed?: SurveyBannerState[],
) {
  let current: SurveyBannerState | null = null;
  const steps: SurveyBannerState[] = [];

  if (bankAccounts.length === 0) {
    current = 'ADD_BANK_ACCOUNT';
    steps.unshift('ADD_BANK_ACCOUNT');
  }

  const experience = investorAccount?.non_accredited_survey_v2?.experience;

  if (
    !experience ||
    experience.experience_alts == null ||
    experience?.experience_bonds == null ||
    experience?.experience_re == null ||
    experience?.experience_stocks == null
  ) {
    current = 'STARTING_YOUR_PROFILE';
    steps.unshift('EXPERIENCE');
  }
  const objective = investorAccount?.non_accredited_survey_v2?.objective;
  const riskProfile = investorAccount?.non_accredited_survey_v2?.risk_profile;

  if (
    !objective ||
    !Object.values(objective).every((obj) => obj != null) ||
    !riskProfile ||
    !Object.values(riskProfile).every((obj) => obj != null)
  ) {
    current = 'STARTING_YOUR_PROFILE';
    steps.unshift('OBJECTIVES');
  }

  if (current === 'STARTING_YOUR_PROFILE' && !viewed?.includes('STARTING_YOUR_PROFILE')) {
    steps.unshift('STARTING_YOUR_PROFILE');
  }

  const ssn = Boolean(investorAccount?.has_bd_account);
  if (!ssn) {
    current = 'VERIFY_IDENTITY';
    steps.unshift('SSN');
    steps.unshift('DOB');
    if (!viewed?.includes('VERIFY_IDENTITY')) {
      steps.unshift('VERIFY_IDENTITY');
    }
  }

  const accredited = investorAccount?.accreditation_self;
  if (accredited == null) {
    steps.unshift('ACCREDITATION');
    current = 'CONFIGURE_INVESTOR';
  }

  const netWorth = investorAccount?.non_accredited_survey_v2?.net_worth?.value != null;
  if (!netWorth) {
    steps.unshift('NET_WORTH');
    current = 'CONFIGURE_INVESTOR';
  }
  const annualIncome = investorAccount?.non_accredited_survey_v2?.annual_income?.value != null;
  if (!annualIncome) {
    steps.unshift('ANNUAL_INCOME');
    current = 'CONFIGURE_INVESTOR';
  }

  if (current === 'CONFIGURE_INVESTOR' && !viewed?.includes('CONFIGURE_INVESTOR')) {
    steps.unshift('CONFIGURE_INVESTOR');
  }

  return { current, steps };
}

export const useSurveyBannerModel = create<SurveyBannerModelProps & SurveyBannerModelActions>((set, get) => ({
  addBankAccount: async (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ): Promise<{ error?: string; success: boolean }> => {
    const investorID = session.get()?.investorId;
    if (!investorID) return { error: 'No investor ID', success: false };
    try {
      const accountInfo = metadata.accounts[0];
      const payload: AddBankAccountViaPlaidMutationVariables = {
        bank_account_id: accountInfo.id,
        bank_account_name: accountInfo.name,
        bank_account_type: accountInfo.type,
        investor_account_id: investorID,
        public_token: publicToken,
      };
      await api.addBankAccount(payload);
      await BankAccountsModel.getState().fetchBankAccounts();
      return { success: true };
    } catch (err: any) {
      logging.logError(err);
      if (err.errors[0].message === BLOCKED_BANK_ACCOUNT_MESSAGE) {
        return {
          error:
            'This bank or financial institution is blocked for security reasons. Please link an account from a different institution',
          success: false,
        };
      }
      return { error: 'Unable to add bank account', success: false };
    }
  },
  currentStep: 0,
  determineAccountSetupState: async () => {
    // TODO: We should start using Amplify's Auth context provider to
    // standardize authentication state access.
    // https://ui.docs.amplify.aws/react/connected-components/authenticator
    try {
      await getCurrentUser();
      set({ isAuthenticated: true });
    } catch (err) {
      return;
    }

    const bankAccounts = BankAccountsModel.getState().bankAccounts;
    const investorAccount = InvestorAccountModel.getState().investorAccount;
    const bannerState = get().determineSurveyBannerState(bankAccounts, investorAccount);
    set({ currentStep: 0, steps: bannerState.steps });
  },
  determineSurveyBannerState,
  getStepInfo(step: SurveyBannerState | null, existingUser = false): StepInfo | null {
    switch (step) {
      case null:
      case undefined:
      case 'SETUP_COMPLETE':
        return null;
      case 'CONFIGURE_INVESTOR':
      case 'ANNUAL_INCOME':
      case 'NET_WORTH':
      case 'ACCREDITATION':
        return {
          alert: existingUser
            ? 'You already have an existing account, but we may need to ask a few more quick questions to use new features.'
            : undefined,
          buttonText: 'Finish Account Setup',
          info: 'We’ll need to understand a little more about you before you can invest.',
          number: 1,
          title: 'Configure Investor Type',
        };
      case 'VERIFY_IDENTITY':
      case 'DOB':
      case 'SSN':
        return {
          buttonText: 'Finish Account Setup',
          info: 'Financial regulations require that we collect information needed to confirm the identities of all investors.  ',
          number: 2,
          title: 'Verify Identity',
        };
      case 'STARTING_YOUR_PROFILE':
      case 'OBJECTIVES':
      case 'EXPERIENCE':
        return {
          buttonText: 'Finish Account Setup',
          info: 'Financial regulations require that we collect information needed to confirm the identities of all investors.  ',
          number: 3,
          title: 'Objectives and Experience',
        };
      case 'ADD_BANK_ACCOUNT':
        return {
          buttonText: 'Finish Account Setup',
          info: 'Finally, you’ll need to add a funding source for your investments.',
          number: 4,
          title: 'Connect a Bank Account',
        };
    }
  },
  isAuthenticated: false,
  loading: false,
  nextStep: () => {
    const nextStep = get().currentStep + 1;
    set({ currentStep: nextStep });
  },
  prevStep: () => {
    const nextStep = get().currentStep - 1;
    if (nextStep >= 0) {
      set({ currentStep: nextStep });
    }
  },
  setLoading: (loading: boolean) => {
    set({ loading });
  },
  setSteps: (steps: SurveyBannerState[]) => {
    set({ steps });
  },
  state: null,
  steps: [],
}));
